import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Space, Button, Spin, message, Tabs } from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  SyncOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { billingAdminService, BillingDashboardStats, Transaction } from '../../services/billingAdminService';
import { downloadBlob, readBlobError } from '../../utils/fileDownload';
import { TransactionAmount } from '../../components/admin';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const BillingDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<BillingDashboardStats | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.type === 'ADMIN') {
      loadDashboardStats();
    }
  }, [user]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load billing stats:', error);
      message.error('Failed to load billing dashboard');
      // Set mock data for development
      setStats({
        totalRevenue: 15420.50,
        totalTransactions: 156,
        successfulTransactions: 142,
        failedTransactions: 14,
        openInvoices: 8,
        paidInvoices: 45,
        activeSubscriptions: 23,
        monthlyRecurringRevenue: 2890.00,
        recentTransactions: [],
        revenueByMonth: [
          { month: 'Jul', revenue: 2100 },
          { month: 'Aug', revenue: 2450 },
          { month: 'Sep', revenue: 2890 },
          { month: 'Oct', revenue: 3200 },
          { month: 'Nov', revenue: 3100 },
          { month: 'Dec', revenue: 1680 }
        ],
        transactionsByStatus: {
          succeeded: 142,
          failed: 14,
          pending: 5,
          refunded: 3
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'succeeded': 'green',
      'pending': 'gold',
      'failed': 'red',
      'refunded': 'purple',
      'paid': 'green',
      'open': 'blue',
      'draft': 'default',
      'void': 'red',
      'active': 'green',
      'cancelled': 'red',
      'past_due': 'orange',
      'trialing': 'cyan'
    };
    return statusColors[status] || 'default';
  };

  const getTypeColor = (type: string) => {
    const typeColors: Record<string, string> = {
      'payment': 'blue',
      'refund': 'orange',
      'authorization': 'purple',
      'topup': 'cyan'
    };
    return typeColors[type] || 'default';
  };

  const recentTransactionsColumns = [
    {
      title: 'Transaction ID',
      dataIndex: 'ExternalID',
      key: 'ExternalID',
      render: (text: string, record: Transaction) => (
        record.Source === 'wallet' || record.Source === 'order' ? (
          <Text>{text}</Text>
        ) : (
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/admin/billing/transactions/${record.TransactionID}`)}
          >
            {text}
          </Button>
        )
      )
    },
    {
      title: 'Customer',
      dataIndex: 'CustomerName',
      key: 'CustomerName',
      render: (name: string, record: Transaction) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.CustomerEmail}</Text>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'Type',
      key: 'Type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type === 'topup' ? 'TOP-UP' : type.toUpperCase()}</Tag>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'Amount',
      key: 'Amount',
      render: (_: number, record: Transaction) => <TransactionAmount record={record} />
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      )
    },
    {
      title: 'Date',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm')
    }
  ];

  // Note: Admin access is enforced by AuthGuard at the route level

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Title level={3}>Failed to Load Billing Dashboard</Title>
        <Button type="primary" onClick={loadDashboardStats}>
          Retry
        </Button>
      </div>
    );
  }

  const successRate = stats.totalTransactions > 0
    ? ((stats.successfulTransactions / stats.totalTransactions) * 100).toFixed(1)
    : '0';

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Billing Dashboard</Title>
          <Text type="secondary">Overview of payments, invoices, and subscriptions</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadDashboardStats}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Revenue Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={stats.totalRevenue}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Monthly Recurring Revenue"
              value={stats.monthlyRecurringRevenue}
              prefix={<SyncOutlined />}
              precision={2}
              valueStyle={{ color: '#ffae00' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={successRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: parseFloat(successRate) >= 90 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Subscriptions"
              value={stats.activeSubscriptions}
              prefix={<CreditCardOutlined />}
              valueStyle={{ color: '#ffae00' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Transaction & Invoice Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Transactions"
              value={stats.totalTransactions}
              valueStyle={{ fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Successful"
              value={stats.successfulTransactions}
              valueStyle={{ fontSize: '20px', color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Failed"
              value={stats.failedTransactions}
              valueStyle={{ fontSize: '20px', color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Open Invoices"
              value={stats.openInvoices}
              valueStyle={{ fontSize: '20px', color: '#1890ff' }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Transaction Status Breakdown */}
        <Col xs={24} lg={8}>
          <Card
            title="Transactions by Status"
            extra={
              <Button type="link" size="small" onClick={() => navigate('/admin/billing/transactions')}>
                View All
              </Button>
            }
          >
            <div style={{ padding: '8px 0' }}>
              {Object.entries(stats.transactionsByStatus || {}).map(([status, count]) => (
                <div key={status} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
                  <Text strong>{count}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Revenue by Month */}
        <Col xs={24} lg={8}>
          <Card
            title="Revenue by Month"
            extra={
              <Button type="link" size="small" onClick={() => navigate('/admin/billing/analytics')}>
                Analytics
              </Button>
            }
          >
            <div style={{ padding: '8px 0' }}>
              {(stats.revenueByMonth || []).slice(-6).map((item) => (
                <div key={item.month} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Text>{item.month}</Text>
                  <Text strong style={{ color: '#3f8600' }}>${item.revenue.toFixed(2)}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Quick Actions */}
        <Col xs={24} lg={8}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                block
                icon={<FileTextOutlined />}
                onClick={() => navigate('/admin/billing/transactions')}
              >
                View All Transactions
              </Button>
              <Button
                block
                icon={<FileTextOutlined />}
                onClick={() => navigate('/admin/billing/invoices')}
              >
                Manage Invoices
              </Button>
              {/* Manage Subscriptions button hidden — see #24 */}
              <Button
                block
                icon={<DownloadOutlined />}
                onClick={async () => {
                  try {
                    const blob = await billingAdminService.exportTransactionsCSV({});
                    downloadBlob(blob, `transactions-${dayjs().format('YYYY-MM-DD')}.csv`);
                    message.success('Export completed');
                  } catch (error) {
                    console.error('Failed to export transactions:', error);
                    message.error(await readBlobError(error, 'Export failed'));
                  }
                }}
              >
                Export Transactions
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recent Transactions */}
      <Card
        title="Recent Transactions"
        style={{ marginTop: '24px' }}
        extra={
          <Space>
            <Button type="primary" size="small" onClick={() => navigate('/admin/billing/transactions')}>
              View All
            </Button>
          </Space>
        }
      >
        <Table
          columns={recentTransactionsColumns}
          dataSource={stats.recentTransactions || []}
          rowKey="TransactionID"
          pagination={{
            pageSize: 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: false
          }}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No recent transactions' }}
        />
      </Card>
    </div>
  );
};

export default BillingDashboard;
